/**
 * Playbook Live's session details, shown under the training panel's status
 * line (the live call's "Audio Settings" shape) as labelled sections: who the
 * rep is meeting and why, the conversation stage, how many of the customer's
 * concerns are handled, and any buying signal to act on. Only the live status
 * line above keeps a dot; these are a description list with clear gaps.
 *
 * Only the track's rep briefing is shown (customer role and meeting purpose,
 * written for the rep when the track was saved), never the agenda: uncovering
 * the customer's concerns is the skill being trained. Before the session
 * starts (`showsSessionProgress` false) only the briefing sections render, so
 * the rep can read them before pressing Start.
 */
import React from 'react';

import type { RepBriefing } from '../../hooks/useTrackRepBriefing';
import {
  AgendaProgressData,
  CloseSignalResolvedData,
  PlaybookStage,
  STAGE_LABELS,
} from '../../services/playbookLiveWebSocketService';
import BriefingSection from './BriefingSection';

const STAGE_ORDER: PlaybookStage[] = ['S1_OPENING', 'S2_DISCOVERY', 'S3_POSITIONING', 'S4_CLOSING', 'S5_WRAP'];
/** The customer has no concerns to raise before this stage; mirrors AGENDA_UNLOCK_STAGE on the server. */
const AGENDA_UNLOCK_STAGE: PlaybookStage = 'S3_POSITIONING';

const COPY = {
  customerRole: 'Customer role',
  meetingPurpose: 'Meeting purpose',
  stage: 'Stage',
  agendaOpen: 'the customer is raising its own concerns now',
  agendaClosed: 'the customer is still getting to know you',
  concernsRaised: 'Concerns raised',
  concernsHandled: 'Concerns handled',
  concernsCount: (satisfied: number, total: number): string => `${satisfied} of ${total}`,
  buyingSignal: 'Buying signal',
  pastSignals: 'Signals so far',
  takeIt: 'Take it.',
  defaultSignal: 'They gave you an opening',
} as const;

const CLOSE_SIGNAL_COPY: Record<string, string> = {
  timeline: 'They mentioned a timeline',
  budget: 'They asked about cost',
  authority: 'They raised who else decides',
  competition: 'They mentioned another supplier',
  stall: 'They tried to stall',
};

interface PlaybookLiveBriefingProps {
  briefing?: RepBriefing;
  stage: PlaybookStage;
  progress: AgendaProgressData | null;
  agendaTotal: number;
  pendingSignal: string | null;
  signals: CloseSignalResolvedData[];
  /** Stage, concerns and signals only mean something once the session runs. */
  showsSessionProgress?: boolean;
}

export const PlaybookLiveBriefing: React.FC<PlaybookLiveBriefingProps> = ({
  briefing,
  stage,
  progress,
  agendaTotal,
  pendingSignal,
  signals,
  showsSessionProgress = true,
}) => {
  const isAgendaOpen = STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf(AGENDA_UNLOCK_STAGE);
  const customerRole = briefing?.customerRole?.trim();
  const meetingPurpose = briefing?.meetingPurpose?.trim();
  const stageLabel = `${STAGE_LABELS[stage] ?? stage}, ${isAgendaOpen ? COPY.agendaOpen : COPY.agendaClosed}`;

  // Pre-session with nothing to brief: no empty, bordered list.
  if (!showsSessionProgress && !customerRole && !meetingPurpose) return null;

  return (
    // One grid so every section stays a direct <dl> child: full-width
    // paragraphs span both columns; Stage spans two rows on the left so both
    // concern counts stack on the right (owner, 2026-09-27).
    <dl
      className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-3 border-t border-hairline pt-3"
      data-testid="playbook-live-briefing"
    >
      {customerRole && (
        <BriefingSection
          label={COPY.customerRole}
          isFullWidth
          text={customerRole}
          testId="playbook-live-customer-role"
        />
      )}
      {meetingPurpose && (
        <BriefingSection
          label={COPY.meetingPurpose}
          isFullWidth
          text={meetingPurpose}
          testId="playbook-live-meeting-purpose"
        />
      )}
      {showsSessionProgress && (
        <>
          <BriefingSection
            label={COPY.stage}
            text={stageLabel}
            testId="playbook-live-stage"
            layoutClassName="row-span-2"
          />
          <BriefingSection
            label={COPY.concernsRaised}
            text={COPY.concernsCount(progress?.raisedSoFar ?? 0, progress?.total ?? agendaTotal ?? 0)}
            testId="playbook-live-raised"
          />
          <BriefingSection
            label={COPY.concernsHandled}
            text={COPY.concernsCount(progress?.satisfied ?? 0, progress?.total ?? agendaTotal ?? 0)}
            testId="playbook-live-agenda"
          />
        </>
      )}
      {showsSessionProgress && pendingSignal && (
        <BriefingSection label={COPY.buyingSignal} isFullWidth testId="playbook-live-pending-signal">
          <span className="font-semibold text-ink">
            {CLOSE_SIGNAL_COPY[pendingSignal] ?? COPY.defaultSignal}. {COPY.takeIt}
          </span>
        </BriefingSection>
      )}
      {showsSessionProgress && signals?.length > 0 && (
        <BriefingSection label={COPY.pastSignals} isFullWidth testId="playbook-live-signals">
          <ul className="flex flex-col gap-0.5">
            {signals?.map((signal) => (
              <li key={signal?.signalId}>
                {CLOSE_SIGNAL_COPY[signal?.type] ?? signal?.type}: {signal?.outcome}
              </li>
            ))}
          </ul>
        </BriefingSection>
      )}
    </dl>
  );
};

export default PlaybookLiveBriefing;
